import { useCallback } from 'react';
import { Recipient } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { BulkMessageHooks } from '@frontend/bulk-message-service';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useNavigate } from '@frontend/weave-router';
import { Button, ConfirmationModal, Text, useAlert, useModalControl } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../hooks/use-show-bulk-message-button';
import { trackingIds } from '../../tracking-ids';

interface Props {
  isLoadingData?: boolean;
  selection?: PracticeAnalyticsTypes.PatientInfo[];
  trackingIdBase?: string;
}

export const BulkMessageButton = ({ isLoadingData, selection = [], trackingIdBase }: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { setPassedRecipients } = BulkMessageHooks.usePassedRecipientsShallowStore('setPassedRecipients');
  const navigate = useNavigate();
  const confirmModalControl = useModalControl();
  const isBulkMessagingEnabled = useShowBulkMessageButton();
  const selectionCount = selection.length;

  const navigaetToBulkMessaging = useCallback(() => {
    const recipients: Recipient[] = [];

    selection.forEach(({ FirstName, id, LastName, locationId, MobilePhone, PreferredName }) => {
      if (MobilePhone && locationId) {
        recipients.push({
          campaignId: '',
          destination: MobilePhone,
          firstName: FirstName,
          lastName: LastName,
          locationId,
          preferredName: PreferredName,
          recipientId: id,
        });
      }
    });

    setPassedRecipients(recipients);
    navigate({
      to: '/bulk/$subNav/$type/$id/$view',
      params: {
        subNav: 'all-campaigns',
        type: 'text',
        id: 'new',
        view: 'editor',
      },
      search: { usePassedRecipients: true },
    });
  }, [navigate, selection, setPassedRecipients]);

  const handleBulkMessageClick = () => {
    if (!selection.length) {
      alert.warning(t('Select patients for bulk messaging'));
      return;
    }

    confirmModalControl.openModal();
  };

  if (!isBulkMessagingEnabled) {
    return null;
  }

  return (
    <>
      <Button
        disabled={isLoadingData || !selectionCount}
        onClick={handleBulkMessageClick}
        style={{ width: 'auto' }}
        trackingId={`practice-analytics-bulk-messaging-${trackingIdBase}`}
      >
        <Icon name='bulk-messaging-small' style={{ marginLeft: 0 }} />
        {t('Bulk Text')}
      </Button>

      <ConfirmationModal
        {...confirmModalControl.modalProps}
        cancelTrackingId={trackingIds.practiceAnalytics.cancelBulkMessage}
        confirmLabel={t('Proceed')}
        confirmTrackingId={trackingIds.practiceAnalytics.confirmBulkMessage}
        message={
          <>
            <Text>
              <Trans t={t}>
                You have selected <strong>{{ selectionCount }} Patients</strong>.
              </Trans>
            </Text>
            <Text>{t('Are you sure you want to proceed with sending bulk text to these patients?')}</Text>
          </>
        }
        onConfirm={navigaetToBulkMessaging}
        title={t('Bulk Text Confirmation')}
      />
    </>
  );
};

BulkMessageButton.displayName = 'BulkMessageButton';
